import React, { useEffect, useState } from "react";
import {
  LuCheckCircle2,
  LuGauge,
  LuLoader2,
  LuLock,
  LuSave,
  LuSearch,
} from "react-icons/lu";
import { isImpersonating, isSupportStaff } from "../../services/auth/impersonation-service";
import { getAccountLimits, searchAccounts, updateAccountLimits } from "../../services/support/support-service";
import { AccountOption, ErrorAlert, StepHeader, card, field, primaryButton } from "./components";

const formatDay = seconds => (seconds
  ? new Date(seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '–');
const formatUsd = usd => (usd == null ? null : `$${Number(usd).toFixed(2)}`);
const formatCount = n => (n == null ? null : Number(n).toLocaleString('en-US'));

// Form values are strings; empty means "no custom limit" (the plan's cap applies)
const toFormValues = limits => ({
  conversations: limits.conversationsLimit != null ? String(limits.conversationsLimit) : '',
  aiUsage: limits.aiUsageLimitUsd != null ? limits.aiUsageLimitUsd.toFixed(2) : '',
});

/** { conversationsLimit, aiUsageLimitCents } for the API, or { error } */
const parseForm = values => {
  const conversations = values.conversations.trim();
  const aiUsage = values.aiUsage.trim();
  if (conversations && !/^\d+$/.test(conversations)) {
    return { error: 'Conversation limit must be a whole number (0 or more).' };
  }
  if (aiUsage && !/^\d+(\.\d{0,2})?$/.test(aiUsage)) {
    return { error: 'AI usage limit must be a dollar amount with at most 2 decimals, e.g. 25 or 12.50.' };
  }
  return {
    conversationsLimit: conversations ? Number(conversations) : null,
    aiUsageLimitCents: aiUsage ? Math.round(Number(aiUsage) * 100) : null,
  };
};

// What applies when a limit field is left empty
const planDefaultText = (cap, included, format, unit) => {
  if (cap != null) return `the plan's cap of ${format(cap)}${unit}`;
  if (included != null) return `no cap: ${format(included)}${unit} included, then billed as overage`;
  return 'no cap';
};

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-xl bg-gray-50 px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wider text-gray-400">{label}</p>
      <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

function LimitField({ id, label, prefix, suffix, value, onChange, placeholder, inputMode, help, warning }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        {prefix && <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400">{prefix}</span>}
        <input
          id={id}
          className={`${field} ${prefix ? 'pl-7' : ''} ${suffix ? 'pr-28' : ''}`}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          inputMode={inputMode}
          autoComplete="off"
        />
        {suffix && <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-gray-400">{suffix}</span>}
      </div>
      <p className="mt-1 text-xs text-gray-500">{help}</p>
      {warning && <p className="mt-1 text-xs font-medium text-amber-600 dark:text-amber-400">{warning}</p>}
    </div>
  );
}

/**
 * Support staff (bc-support): find a customer account by email and set its custom conversation and AI usage limits,
 * which replace the plan's caps (billing/account-limits). Changes apply at once: lowering a limit below this period's
 * usage blocks the account, raising one it is blocked on unblocks it.
 */
export default function SubscriptionUpdatePage() {
  const [staff, setStaff] = useState(null);
  const [query, setQuery] = useState('');
  const [accounts, setAccounts] = useState(null);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [values, setValues] = useState({ conversations: '', aiUsage: '' });
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    isSupportStaff().then(setStaff);
  }, []);

  useEffect(() => {
    if (!selected) {
      setDetails(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setSaved(false);
    setError(null);
    getAccountLimits(selected.accountId).then(({ response, status }) => {
      if (cancelled) return;
      if (status === 200) {
        setDetails(response);
        setValues(toFormValues(response.limits));
      } else {
        setDetails(null);
        setError(response || 'Could not load the account limits');
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [selected]);

  const handleSearch = async e => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setError(null);
    setSelected(null);
    const { response, status } = await searchAccounts({ email: query.trim() });
    if (status === 200) {
      const found = response.accounts || [];
      setAccounts(found);
      if (found.length === 1) setSelected(found[0]);
    } else {
      setAccounts(null);
      setError(response || 'Search failed');
    }
    setSearching(false);
  };

  const parsed = parseForm(values);
  const dirty = details && (values.conversations !== toFormValues(details.limits).conversations
    || values.aiUsage !== toFormValues(details.limits).aiUsage);

  const handleSave = async e => {
    e.preventDefault();
    if (parsed.error || !dirty) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    const { response, status } = await updateAccountLimits(selected.accountId, parsed);
    if (status === 200) {
      setDetails(response);
      setValues(toFormValues(response.limits));
      setSaved(true);
    } else {
      setError(response || 'Could not save the limits');
    }
    setSaving(false);
  };

  if (staff === null) {
    return (
      <div role="status" className="flex items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
      </div>
    );
  }

  if (!staff || isImpersonating()) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100">
          <LuLock className="h-5 w-5 text-gray-500" aria-hidden="true" />
        </span>
        <h1 className="text-lg font-semibold text-gray-900">Support access only</h1>
        <p className="mt-1 text-sm text-gray-500">
          {staff ? 'End the impersonation session to change account limits.' : 'This page is only available to BotCircuits support staff.'}
        </p>
      </div>
    );
  }

  const usage = details?.usage;
  const planLimits = details?.planLimits;
  const newConversations = parsed.error ? null : parsed.conversationsLimit;
  const newAiCents = parsed.error ? null : parsed.aiUsageLimitCents;
  const blocksConversations = usage && newConversations != null && usage.conversations > newConversations;
  const blocksAi = usage && newAiCents != null && usage.aiUsageUsd * 100 >= newAiCents;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
      <div className="mb-8 flex items-start gap-4">
        <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-gray-900 text-brand-lime shadow-sm">
          <LuGauge className="h-6 w-6" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Subscription update</h1>
          <p className="mt-1 text-sm text-gray-500">
            Set a customer's own conversation and AI usage limits per billing period. They replace the plan's caps and apply immediately.
          </p>
        </div>
      </div>

      {error && <div className="mb-4"><ErrorAlert message={error} onDismiss={() => setError(null)} /></div>}

      <div className="space-y-5">
        <section className={card} aria-labelledby="limits-find">
          <StepHeader step={1} title={<span id="limits-find">Find the customer account</span>} description="Search by the email of any user in the account." />

          <form onSubmit={handleSearch} className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <LuSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                aria-label="Email"
                type="email"
                className={`${field} pl-10`}
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="owner@customer.com"
                autoComplete="off"
                autoFocus
              />
            </div>
            <button type="submit" className={primaryButton} disabled={searching || !query.trim()}>
              {searching
                ? <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                : <LuSearch className="h-4 w-4" aria-hidden="true" />}
              Search
            </button>
          </form>

          {accounts && accounts.length === 0 && (
            <div className="mt-5 rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center">
              <p className="text-sm font-medium text-gray-900">No matching account</p>
              <p className="mt-1 text-sm text-gray-500">Check the email address.</p>
            </div>
          )}

          {accounts && accounts.length > 0 && (
            <div className="mt-5">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-400">
                {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'} found
              </p>
              <div role="radiogroup" aria-label="Customer account" className="space-y-2">
                {accounts.map(account => (
                  <AccountOption
                    key={account.accountId}
                    account={account}
                    selected={selected?.accountId === account.accountId}
                    onSelect={setSelected}
                  />
                ))}
              </div>
            </div>
          )}
        </section>

        {selected && (
          <section className={card} aria-labelledby="limits-set">
            <StepHeader step={2} title={<span id="limits-set">Set usage limits</span>} description={`For ${selected.name || selected.accountId}. Leave a field empty to use the plan's default.`} />

            {loading && (
              <div role="status" className="flex items-center gap-2 py-6 text-sm text-gray-500">
                <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading limits…
              </div>
            )}

            {!loading && details && (
              <form onSubmit={handleSave} className="space-y-5">
                <div className="grid gap-3 sm:grid-cols-3">
                  <Stat
                    label="Plan"
                    value={details.planDisplayName}
                    hint={`Period ${formatDay(details.period.start)} – ${formatDay(details.period.end)}`}
                  />
                  <Stat
                    label="Conversations used"
                    value={formatCount(usage.conversations)}
                    hint={details.effective.conversations != null ? `limit ${formatCount(details.effective.conversations)}` : 'no limit'}
                  />
                  <Stat
                    label="AI usage used"
                    value={formatUsd(usage.aiUsageUsd)}
                    hint={details.effective.aiUsageUsd != null ? `limit ${formatUsd(details.effective.aiUsageUsd)}` : 'no limit'}
                  />
                </div>

                {details.legacy && (
                  <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                    This account is on a legacy plan, which is never capped: limits saved here only apply once it moves to a current plan.
                  </p>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  <LimitField
                    id="limit-conversations"
                    label="Conversation limit"
                    suffix="per period"
                    value={values.conversations}
                    onChange={v => { setSaved(false); setValues(prev => ({ ...prev, conversations: v })); }}
                    placeholder="Plan default"
                    inputMode="numeric"
                    help={`Empty: ${planDefaultText(planLimits.conversations, planLimits.includedConversations, formatCount, ' conversations')}.`}
                    warning={blocksConversations ? `Below the ${formatCount(usage.conversations)} used: the agents stop replying as soon as you save.` : null}
                  />
                  <LimitField
                    id="limit-ai-usage"
                    label="AI usage limit"
                    prefix="$"
                    suffix="per period"
                    value={values.aiUsage}
                    onChange={v => { setSaved(false); setValues(prev => ({ ...prev, aiUsage: v })); }}
                    placeholder="Plan default"
                    inputMode="decimal"
                    help={`Empty: ${planDefaultText(planLimits.aiUsageUsd, planLimits.includedAiUsageUsd, formatUsd, '')}.`}
                    warning={blocksAi ? `At or below the ${formatUsd(usage.aiUsageUsd)} used: AI features (the copilot) stop as soon as you save.` : null}
                  />
                </div>

                {parsed.error && (values.conversations || values.aiUsage) && (
                  <p className="text-sm text-red-600 dark:text-red-400">{parsed.error}</p>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-gray-400">
                    {details.limits.updatedAt
                      ? `Last changed ${formatDay(details.limits.updatedAt)}${details.limits.updatedBy ? ` by ${details.limits.updatedBy}` : ''}`
                      : 'No custom limits set'}
                  </p>
                  <div className="flex items-center gap-3">
                    {saved && (
                      <span role="status" className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600 dark:text-green-400">
                        <LuCheckCircle2 className="h-4 w-4" aria-hidden="true" /> Saved
                      </span>
                    )}
                    <button type="submit" className={primaryButton} disabled={saving || !dirty || !!parsed.error}>
                      {saving
                        ? <LuLoader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                        : <LuSave className="h-4 w-4" aria-hidden="true" />}
                      Save limits
                    </button>
                  </div>
                </div>
              </form>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
